import { blogSlugify } from "@complete-athlete/enums";

export type BlogHeading = {
  id: string;
  text: string;
  level: 2 | 3;
  /** The heading's line in the Markdown, 1-based: how the rendered article finds the same id. */
  line: number;
};

/** A heading's words without Markdown's marks: `**bold**`, `` `code` `` and `[links](…)` read as their text. */
export const plainHeadingText = (raw: string) =>
  raw
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/[*_`~]/g, "")
    .replace(/\s+#+\s*$/, "")
    .trim();

/** Hands out the anchor of each heading in reading order: its words as a slug, with `-2`, `-3` after a repeat. */
export const headingIds = () => {
  const seen = new Map<string, number>();
  return (text: string) => {
    const base = blogSlugify(text) || "section";
    const count = (seen.get(base) ?? 0) + 1;
    seen.set(base, count);
    return count === 1 ? base : `${base}-${count}`;
  };
};

/**
 * The `##` and `###` headings of a post, in order, for its table of contents and the anchors of
 * the rendered article. Code blocks are skipped.
 */
export const blogHeadings = (markdown: string): BlogHeading[] => {
  const nextId = headingIds();
  const headings: BlogHeading[] = [];
  let fenced = false;
  for (const [index, line] of markdown.split("\n").entries()) {
    if (/^\s*(```|~~~)/.test(line)) {
      fenced = !fenced;
      continue;
    }
    if (fenced) continue;
    const match = /^(#{2,3})\s+(.+)$/.exec(line.trim());
    if (!match) continue;
    const text = plainHeadingText(match[2]!);
    if (!text) continue;
    headings.push({ id: nextId(text), text, level: match[1]!.length as 2 | 3, line: index + 1 });
  }
  return headings;
};

/** Words in a post's Markdown, for the article's structured data. Pictures and code count as nothing. */
export const blogWordCount = (markdown: string) =>
  markdown
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/[#>*_`~|-]/g, " ")
    .split(/\s+/)
    .filter(Boolean).length;
